import type { CSSProperties, ReactNode } from 'react'
import { useSvgIds } from './cardSvg'

/*
 * Candidate pictures for the editor's tools, in the kit's language: chunky ink strokes (#1d2b36),
 * a little colour, readable at 30-40 px, no text. Several candidates per tool (A, B, C) to compare
 * on /kit-lab; the chosen ones move into the chrome and the rest go. Every icon takes `color` (the
 * ink) and `size` (px, default 40); all are drawn on a 40 x 40 grid.
 */

const INK = '#1d2b36'

export type ToolIconProps = { color?: string; size?: number }

function Icon({ size = 40, children, style }: { size?: number; children: ReactNode; style?: CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden style={{ pointerEvents: 'none', overflow: 'visible', ...style }}>
      {children}
    </svg>
  )
}

// ---------------------------------------------------------------- erase

/** Erase A: a two-tone eraser rubbing out a line that breaks into crumbs behind it. */
export function EraseIconA({ color = INK, size }: ToolIconProps) {
  return (
    <Icon size={size}>
      <path d="M3 34 h8" stroke={color} strokeWidth="3" strokeLinecap="round" />
      <circle cx="15" cy="34" r="1.6" fill={color} opacity="0.55" />
      <circle cx="19.5" cy="35" r="1.3" fill={color} opacity="0.35" />
      <g transform="rotate(-35 24 18)">
        <rect x="12" y="11" width="24" height="14" rx="3.5" fill="#f6a5b8" stroke={color} strokeWidth="2.4" />
        <path d="M24 11 h8.5 a3.5 3.5 0 0 1 3.5 3.5 v7 a3.5 3.5 0 0 1 -3.5 3.5 h-8.5 Z" fill="#8fc8f0" stroke={color} strokeWidth="2.4" strokeLinejoin="round" />
      </g>
    </Icon>
  )
}

/** Erase B: a block going pop: a dashed ghost of a cube with a puff of bits flying off it. */
export function EraseIconB({ color = INK, size }: ToolIconProps) {
  return (
    <Icon size={size}>
      <rect x="8" y="12" width="20" height="20" rx="3" fill="rgba(255,255,255,0.6)" stroke={color} strokeWidth="2.4" strokeDasharray="4.5 3.5" />
      <g fill="#f28da0" stroke={color} strokeWidth="1.8">
        <rect x="27" y="4" width="6" height="6" rx="1.2" transform="rotate(20 30 7)" />
        <rect x="32" y="15" width="5" height="5" rx="1" transform="rotate(-15 34.5 17.5)" />
      </g>
      <rect x="20" y="1" width="4.5" height="4.5" rx="1" fill="#ffd166" stroke={color} strokeWidth="1.6" />
      <path d="M26 10 l3 -2 M28 14 l3 0" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </Icon>
  )
}

/** Erase C: a soapy sponge wiping, with bubbles. */
export function EraseIconC({ color = INK, size }: ToolIconProps) {
  return (
    <Icon size={size}>
      <path d="M4 34 q 6 -5 12 0 t 12 0" fill="none" stroke="#8fc8f0" strokeWidth="3" strokeLinecap="round" />
      <rect x="9" y="12" width="24" height="15" rx="5" fill="#ffd166" stroke={color} strokeWidth="2.4" transform="rotate(-12 21 19.5)" />
      <g fill="#ffb21f" opacity="0.9">
        <circle cx="15" cy="20" r="1.6" />
        <circle cx="21" cy="17" r="1.4" />
        <circle cx="26" cy="20" r="1.6" />
      </g>
      <g fill="#ffffff" stroke="#5b8fc4" strokeWidth="1.8">
        <circle cx="8" cy="8" r="3.6" />
        <circle cx="16" cy="5" r="2.4" />
        <circle cx="34" cy="7" r="2.8" />
      </g>
    </Icon>
  )
}

// ---------------------------------------------------------------- brush size

/**
 * Brush size A: three dots growing left to right. `level` (0..1, optional) fills the dots up to the
 * current size and leaves the rest pale, so the icon is also the setting.
 */
export function SizeIconA({ color = INK, size, level }: ToolIconProps & { level?: number }) {
  const dots: [number, number][] = [[6, 2.6], [15, 4.6], [28, 8]]
  const on = level == null ? 3 : Math.round(level * 2) + 1
  return (
    <Icon size={size}>
      {dots.map(([cx, r], i) => (
        <circle key={i} cx={cx} cy={22} r={r} fill={i < on ? color : 'rgba(29,43,54,0.2)'} />
      ))}
    </Icon>
  )
}

/**
 * Brush size, one shape: a single ink dot whose diameter is the setting. `level` (0..1) runs from a
 * small but legible dot to one nearly filling the icon.
 */
export function SizeIconDot({ color = INK, size, level = 1 }: ToolIconProps & { level?: number }) {
  const t = Math.max(0, Math.min(1, level))
  return (
    <Icon size={size}>
      <circle cx="20" cy="20" r={3.5 + 13 * t} fill={color} />
    </Icon>
  )
}

/**
 * Brush size as a rounded wedge (owner: "a wedge, but solid black and rounded ... filled in partly
 * based on current size"): thin round end on the left, fat round end on the right; solid ink up to
 * `level` (0..1) of its length, the rest a faint wedge, so the fill grows with the brush.
 */
export function SizeIconWedge({ color = INK, size, level = 1 }: ToolIconProps & { level?: number }) {
  const id = useSvgIds()
  const t = Math.max(0, Math.min(1, level))
  // Outer tangents of a r=2 circle at (5,20) and a r=9 circle at (29,20).
  const d = 'M4.42 18.08 L26.37 11.37 A9 9 0 1 1 26.37 28.63 L4.42 21.92 A2 2 0 0 1 4.42 18.08 Z'
  // The filled part reaches from the thin end (x 3) to the fat end (x 38); never less than the tip.
  const x = 3 + (0.12 + 0.88 * t) * 35
  return (
    <Icon size={size}>
      <defs>
        <clipPath id={id('fill')}><rect x="0" y="0" width={x} height="40" /></clipPath>
      </defs>
      <path d={d} fill={color} opacity="0.18" />
      <path d={d} fill={color} clipPath={`url(#${id('fill')})`} />
    </Icon>
  )
}

/** Brush size B: a wedge, thin to thick (like the kit's sliders), with a round brush tip at its fat end. */
export function SizeIconB({ color = INK, size, paint = '#ef476f' }: ToolIconProps & { paint?: string }) {
  return (
    <Icon size={size}>
      <path d="M4 21 L26 13 L26 29 Z" fill={paint} stroke={color} strokeWidth="2.2" strokeLinejoin="round" />
      <circle cx="29" cy="21" r="8.5" fill={paint} stroke={color} strokeWidth="2.4" />
      <circle cx="4" cy="21" r="1.8" fill={color} />
    </Icon>
  )
}

/** Brush size C: a small dot inside a big ring (a stamp's footprint), with an arrow pushing it outward. */
export function SizeIconC({ color = INK, size }: ToolIconProps) {
  return (
    <Icon size={size}>
      <circle cx="18" cy="22" r="14" fill="rgba(239,71,111,0.22)" stroke={color} strokeWidth="2.4" strokeDasharray="5 3.5" />
      <circle cx="18" cy="22" r="5" fill={color} />
      <path d="M24 16 L34 6 M34 6 h-6 M34 6 v6" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </Icon>
  )
}

// ---------------------------------------------------------------- add to palette

/** Add A: a blob of paint with a round plus badge. `paint` is the blob's colour (the current colour). */
export function AddIconA({ color = INK, size, paint = '#4cc9f0' }: ToolIconProps & { paint?: string }) {
  return (
    <Icon size={size}>
      <path d="M8 18 c -2 -9 9 -14 15 -9 c 6 -2 11 4 8 10 c 4 5 -2 13 -9 11 c -5 4 -14 1 -13 -5 c -4 -1 -4 -6 -1 -7 z" fill={paint} stroke={color} strokeWidth="2.4" strokeLinejoin="round" />
      <circle cx="30" cy="30" r="8" fill="#ffffff" stroke={color} strokeWidth="2.4" />
      <path d="M30 26 v8 M26 30 h8" stroke={color} strokeWidth="2.8" strokeLinecap="round" />
    </Icon>
  )
}

/** Add B: a painter's palette with three dabs, the last an empty dashed one with a plus in it. */
export function AddIconB({ color = INK, size }: ToolIconProps) {
  return (
    <Icon size={size}>
      <path d="M20 4 C 9 4 3 12 3 20 c 0 8 6 15 15 15 c 4 0 5 -3 3 -6 c -2 -3 0 -6 4 -6 h 5 c 4 0 7 -3 7 -7 C 37 10 30 4 20 4 z" fill="#f2d8b0" stroke={color} strokeWidth="2.4" strokeLinejoin="round" />
      <circle cx="11" cy="19" r="3.4" fill="#ef476f" />
      <circle cx="16" cy="11" r="3.4" fill="#ffd166" />
      <circle cx="25" cy="10" r="3.4" fill="#4cc9f0" />
      <circle cx="30" cy="31" r="7.5" fill="#ffffff" stroke={color} strokeWidth="2.2" />
      <path d="M30 27.5 v7 M26.5 31 h7" stroke={color} strokeWidth="2.6" strokeLinecap="round" />
    </Icon>
  )
}

/** Add C: a dropper letting a drop fall into a dashed, empty swatch (fill a new swatch). */
export function AddIconC({ color = INK, size, paint = '#7bd389' }: ToolIconProps & { paint?: string }) {
  return (
    <Icon size={size}>
      <rect x="4" y="22" width="15" height="15" rx="4" fill="#ffffff" stroke={color} strokeWidth="2.2" strokeDasharray="4 3" />
      <path d="M11.5 13 c 2.5 3 3.5 4.6 3.5 6 a3.5 3.5 0 0 1 -7 0 c 0 -1.4 1 -3 3.5 -6 z" fill={paint} stroke={color} strokeWidth="1.8" />
      <g transform="rotate(40 26 14)">
        <rect x="22" y="2" width="8" height="8" rx="3.5" fill="#ef476f" stroke={color} strokeWidth="2.2" />
        <path d="M23.5 10 h5 v12 l -2.5 4 l -2.5 -4 z" fill="#e8f3fb" stroke={color} strokeWidth="2.2" strokeLinejoin="round" />
        <path d="M24.5 17 h3 v4.5 l -1.5 2.5 l -1.5 -2.5 z" fill={paint} />
      </g>
    </Icon>
  )
}

// ---------------------------------------------------------------- settings

/** Settings A: a chunky cog with a coloured hub. */
export function SettingsIconA({ color = INK, size, hub = '#ffd166' }: ToolIconProps & { hub?: string }) {
  const teeth = Array.from({ length: 8 }, (_, i) => i * 45)
  return (
    <Icon size={size}>
      <g fill="#c3ccd4" stroke={color} strokeWidth="2.4" strokeLinejoin="round">
        {teeth.map((a) => (
          <rect key={a} x="16.5" y="3" width="7" height="8" rx="1.8" transform={`rotate(${a} 20 20)`} />
        ))}
        <circle cx="20" cy="20" r="12" />
      </g>
      {/* hide the teeth's inner edges under the ring */}
      <circle cx="20" cy="20" r="10.6" fill="#c3ccd4" />
      <circle cx="20" cy="20" r="5.5" fill={hub} stroke={color} strokeWidth="2.4" />
    </Icon>
  )
}

/** Settings B: three upright mixing-desk sliders with coloured knobs at different heights. */
export function SettingsIconB({ color = INK, size }: ToolIconProps) {
  const knobs: [number, number, string][] = [[10, 24, '#ef476f'], [20, 13, '#ffd166'], [30, 21, '#4cc9f0']]
  return (
    <Icon size={size}>
      {knobs.map(([x, y, fill]) => (
        <g key={x}>
          <line x1={x} y1="5" x2={x} y2="35" stroke={color} strokeWidth="2.6" strokeLinecap="round" />
          <rect x={x - 4.5} y={y - 3.5} width="9" height="7" rx="2.5" fill={fill} stroke={color} strokeWidth="2.3" />
        </g>
      ))}
    </Icon>
  )
}

/** Settings C: a big round dial knob with tick marks around it and a coloured pointer. */
export function SettingsIconC({ color = INK, size, pointer = '#ef476f' }: ToolIconProps & { pointer?: string }) {
  const ticks = [-120, -80, -40, 0, 40, 80, 120]
  return (
    <Icon size={size}>
      {ticks.map((a) => (
        <line key={a} x1="20" y1="2.5" x2="20" y2="6" stroke={color} strokeWidth="2.4" strokeLinecap="round" transform={`rotate(${a} 20 21)`} />
      ))}
      <circle cx="20" cy="21" r="12.5" fill="#ffffff" stroke={color} strokeWidth="2.5" />
      <circle cx="20" cy="21" r="8" fill="#e8edf2" />
      <line x1="20" y1="21" x2="26" y2="13" stroke={pointer} strokeWidth="3.6" strokeLinecap="round" />
      <circle cx="20" cy="21" r="2.6" fill={color} />
    </Icon>
  )
}

// ---------------------------------------------------------------- undo / redo

/** Undo A: a fat hooked arrow in a warm colour with an ink outline (redo is its mirror). */
export function UndoIconA({ color = INK, size, fill = '#ffb21f', mirrored }: ToolIconProps & { fill?: string; mirrored?: boolean }) {
  return (
    <Icon size={size} style={mirrored ? { transform: 'scaleX(-1)' } : undefined}>
      <path
        d="M14 7 L3 17 L14 27 V20 H23 a6 6 0 0 1 0 12 H18 v6 h5 a12 12 0 0 0 0 -24 H14 Z"
        fill={fill} stroke={color} strokeWidth="2.4" strokeLinejoin="round"
      />
    </Icon>
  )
}
export const RedoIconA = (p: ToolIconProps & { fill?: string }) => <UndoIconA {...p} mirrored />

/** Undo B: a circle arrow running backwards (round we go, back where we were). */
export function UndoIconB({ color = INK, size, mirrored }: ToolIconProps & { mirrored?: boolean }) {
  return (
    <Icon size={size} style={mirrored ? { transform: 'scaleX(-1)' } : undefined}>
      <path d="M10.5 11 A 12 12 0 1 1 8 25" fill="none" stroke={color} strokeWidth="3.6" strokeLinecap="round" />
      <path d="M4 5 L5.5 15 L15 12 Z" fill={color} stroke={color} strokeWidth="2" strokeLinejoin="round" />
      <circle cx="20" cy="20" r="3.4" fill="#4cc9f0" stroke={color} strokeWidth="2" />
    </Icon>
  )
}
export const RedoIconB = (p: ToolIconProps) => <UndoIconB {...p} mirrored />

/** Undo C: rewind, two chunky triangles pointing back (the tape-player button); redo is fast-forward. */
export function UndoIconC({ color = INK, size, fill = '#7bd389', mirrored }: ToolIconProps & { fill?: string; mirrored?: boolean }) {
  return (
    <Icon size={size} style={mirrored ? { transform: 'scaleX(-1)' } : undefined}>
      <g fill={fill} stroke={color} strokeWidth="2.4" strokeLinejoin="round">
        <path d="M20 9 L4 20 L20 31 Z" />
        <path d="M36 9 L20 20 L36 31 Z" />
      </g>
    </Icon>
  )
}
export const RedoIconC = (p: ToolIconProps & { fill?: string }) => <UndoIconC {...p} mirrored />

// ---------------------------------------------------------------- home

/** Home A: a little house with a red roof, a door and a window, filled in. */
export function HomeIconA({ color = INK, size, roof = '#ef476f' }: ToolIconProps & { roof?: string }) {
  return (
    <Icon size={size}>
      <rect x="8" y="17" width="24" height="18" rx="1.5" fill="#fbf7ee" stroke={color} strokeWidth="2.4" />
      <path d="M4 19 L20 5 L36 19" fill={roof} stroke={color} strokeWidth="2.6" strokeLinejoin="round" strokeLinecap="round" />
      <rect x="16" y="24" width="8" height="11" rx="1.5" fill="#ffd166" stroke={color} strokeWidth="2.2" />
    </Icon>
  )
}

/** Home B: the kit's outline house with a chimney and a lit window. */
export function HomeIconB({ color = INK, size }: ToolIconProps) {
  return (
    <Icon size={size}>
      <path d="M26 9 V5 h4 v8" fill="#c3ccd4" stroke={color} strokeWidth="2.4" strokeLinejoin="round" />
      <path d="M4 20 L20 6 L36 20" fill="none" stroke={color} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 17 V35 H31 V17" fill="none" stroke={color} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="15.5" y="21" width="9" height="8" rx="1.5" fill="#ffd166" stroke={color} strokeWidth="2.2" />
    </Icon>
  )
}

/** Home C: all the pages, four little coloured cards in a grid (home is where the pages are). */
export function HomeIconC({ color = INK, size }: ToolIconProps) {
  const cards: [number, number, string][] = [[5, 5, '#ef476f'], [21, 5, '#ffd166'], [5, 21, '#4cc9f0'], [21, 21, '#7bd389']]
  return (
    <Icon size={size}>
      {cards.map(([x, y, fill]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="14" height="14" rx="4" fill={fill} stroke={color} strokeWidth="2.4" />
      ))}
    </Icon>
  )
}

// ---------------------------------------------------------------- materials

/**
 * A ball of `paint` in a material, for the colour picker's material buttons: soft (matte, no
 * shine), glossy (a soft highlight), shiny (a sharp highlight and a bright rim), metallic (banded
 * reflections in the paint's tint).
 */
export function MaterialBall({ material, paint, size = 40 }: { material: 'soft' | 'glossy' | 'shiny' | 'metallic'; paint: string; size?: number }) {
  const id = useSvgIds()
  return (
    <Icon size={size}>
      <defs>
        <radialGradient id={id('body')} cx="0.38" cy="0.34" r="0.75">
          <stop offset="0" stopColor="#ffffff" stopOpacity={material === 'soft' ? 0.28 : 0.18} />
          <stop offset="0.55" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="1" stopColor="#000000" stopOpacity={material === 'soft' ? 0.3 : 0.42} />
        </radialGradient>
        <linearGradient id={id('metal')} x1="0" y1="0" x2="0.25" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.75" />
          <stop offset="0.3" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="0.48" stopColor="#000000" stopOpacity="0.45" />
          <stop offset="0.56" stopColor="#ffffff" stopOpacity="0.7" />
          <stop offset="0.8" stopColor="#000000" stopOpacity="0.2" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.5" />
        </linearGradient>
        <radialGradient id={id('gloss')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="20" cy="20" r="16" fill={paint} />
      <circle cx="20" cy="20" r="16" fill={`url(#${id(material === 'metallic' ? 'metal' : 'body')})`} />
      {material === 'glossy' && <ellipse cx="14.5" cy="13" rx="6.5" ry="4.5" fill={`url(#${id('gloss')})`} transform="rotate(-30 14.5 13)" />}
      {material === 'shiny' && (
        <>
          <ellipse cx="14" cy="12.5" rx="4.6" ry="2.8" fill="#ffffff" transform="rotate(-30 14 12.5)" />
          <circle cx="25.5" cy="10.5" r="1.4" fill="#ffffff" />
          <path d="M8 26 A 13 13 0 0 0 30 30" fill="none" stroke="#ffffff" strokeOpacity="0.55" strokeWidth="1.6" strokeLinecap="round" />
        </>
      )}
      {material === 'metallic' && <ellipse cx="13" cy="11.5" rx="3" ry="1.6" fill="#ffffff" transform="rotate(-30 13 11.5)" />}
      <circle cx="20" cy="20" r="16" fill="none" stroke={INK} strokeWidth="2.2" vectorEffect="non-scaling-stroke" />
    </Icon>
  )
}
